import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Smartphone, Moon, Bell } from "lucide-react";

/**
 * Iter-173: per-user push notification preferences.
 *
 * Lets each user choose which notification CATEGORIES they want pushed to their device(s)
 * and define quiet hours during which pushes are suppressed (in-app delivery is unaffected).
 *
 * Backend: GET/PUT /api/push/me/prefs
 * Storage: users.<doc>.push_prefs = { disabled_categories, disabled_keys, quiet_hours_enabled,
 *          quiet_start_hour, quiet_end_hour, quiet_tz }
 */
const CATEGORIES = ["CRA", "Missions", "Clients", "Billing", "Freelance", "Account"];

export default function MyPushPrefsPanel() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/push/me/prefs");
        setPrefs(r.data?.push_prefs || null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const patch = async (partial) => {
    const optimistic = { ...prefs, ...partial };
    setPrefs(optimistic);
    try {
      const r = await api.put("/push/me/prefs", partial);
      setPrefs(r.data?.push_prefs || optimistic);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
      // reload truth from server
      const r = await api.get("/push/me/prefs");
      setPrefs(r.data?.push_prefs || null);
    }
  };

  const toggleCategory = (cat) => {
    const cur = new Set(prefs?.disabled_categories || []);
    if (cur.has(cat)) cur.delete(cat);
    else cur.add(cat);
    patch({ disabled_categories: Array.from(cur) });
  };

  if (loading || !prefs) {
    return (
      <div className="mb-6 p-4 bg-blue-50/40 border border-blue-200 rounded-md text-xs text-zinc-500">Loading push prefs…</div>
    );
  }

  const disabledSet = new Set(prefs.disabled_categories || []);

  return (
    <div className="mb-6 p-4 bg-blue-50/40 border border-blue-200 rounded-md" data-testid="my-push-prefs-panel">
      <div className="flex items-center gap-2 mb-2">
        <Smartphone size={14} className="text-blue-700" />
        <span className="text-[11px] uppercase tracking-[0.2em] text-blue-800 font-mono">My push notifications</span>
      </div>
      <p className="text-xs text-blue-900/80 leading-relaxed mb-3 max-w-2xl">
        These settings only affect <strong>push notifications on your devices</strong>. In-app notifications and emails are
        controlled separately below. Toggle a category off to silence its pushes; toggle quiet hours to suppress all
        pushes overnight.
      </p>

      {/* Per-category toggles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
        {CATEGORIES.map((cat) => {
          const enabled = !disabledSet.has(cat);
          return (
            <label
              key={cat}
              data-testid={`my-push-cat-${cat.toLowerCase()}`}
              className={`flex items-center justify-between gap-2 px-3 h-10 rounded-md text-sm border cursor-pointer transition-colors ${
                enabled
                  ? "bg-white border-blue-200 text-blue-900 hover:bg-blue-50"
                  : "bg-zinc-50 border-zinc-200 text-zinc-500 hover:bg-zinc-100"
              }`}
            >
              <span className="flex items-center gap-2">
                <Bell size={13} className={enabled ? "text-blue-600" : "text-zinc-400"} />
                {cat}
              </span>
              <Switch checked={enabled} onCheckedChange={() => toggleCategory(cat)} />
            </label>
          );
        })}
      </div>

      {/* Quiet hours */}
      <div className="bg-white border border-blue-200 rounded-md px-4 py-3" data-testid="my-push-quiet-hours">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Moon size={14} className="text-indigo-600" />
            <span className="text-sm font-medium text-zinc-800">Quiet hours</span>
            <span className="text-[11px] text-zinc-500">— suppresses pushes only, in-app remains</span>
          </div>
          <Switch
            checked={!!prefs.quiet_hours_enabled}
            onCheckedChange={(v) => patch({ quiet_hours_enabled: v })}
            data-testid="my-push-quiet-toggle"
          />
        </div>
        {prefs.quiet_hours_enabled && (
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <label className="text-xs text-zinc-600 flex items-center gap-1.5">
              From
              <select
                data-testid="my-push-quiet-start"
                value={prefs.quiet_start_hour}
                onChange={(e) => patch({ quiet_start_hour: parseInt(e.target.value, 10) })}
                className="border border-zinc-300 rounded-md h-8 px-2 text-sm bg-white"
              >
                {Array.from({ length: 24 }).map((_, h) => (
                  <option key={h} value={h}>{String(h).padStart(2, "0")}h</option>
                ))}
              </select>
            </label>
            <label className="text-xs text-zinc-600 flex items-center gap-1.5">
              To
              <select
                data-testid="my-push-quiet-end"
                value={prefs.quiet_end_hour}
                onChange={(e) => patch({ quiet_end_hour: parseInt(e.target.value, 10) })}
                className="border border-zinc-300 rounded-md h-8 px-2 text-sm bg-white"
              >
                {Array.from({ length: 24 }).map((_, h) => (
                  <option key={h} value={h}>{String(h).padStart(2, "0")}h</option>
                ))}
              </select>
            </label>
            <label className="text-xs text-zinc-600 flex items-center gap-1.5">
              Timezone
              <select
                data-testid="my-push-quiet-tz"
                value={prefs.quiet_tz}
                onChange={(e) => patch({ quiet_tz: e.target.value })}
                className="border border-zinc-300 rounded-md h-8 px-2 text-sm bg-white"
              >
                <option value="Europe/Paris">Europe/Paris</option>
                <option value="Africa/Casablanca">Africa/Casablanca</option>
                <option value="UTC">UTC</option>
                <option value="Europe/London">Europe/London</option>
                <option value="America/New_York">America/New_York</option>
              </select>
            </label>
            <span className="text-[11px] text-zinc-500">
              {prefs.quiet_start_hour === prefs.quiet_end_hour
                ? "(empty window — disabled)"
                : prefs.quiet_start_hour < prefs.quiet_end_hour
                ? `Active ${String(prefs.quiet_start_hour).padStart(2, "0")}h–${String(prefs.quiet_end_hour).padStart(2, "0")}h`
                : `Active ${String(prefs.quiet_start_hour).padStart(2, "0")}h–${String(prefs.quiet_end_hour).padStart(2, "0")}h (next day)`}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
